import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAuthorization } from '@/entities/session';
import range from 'lodash/range';
import { validateSchema } from '@/shared/libs/validate';
import {
  SmsConfirmSchema,
  emailFormSchemaWithAuth,
  resetPasswordWithPhoneSchema,
  emailFormSchemaWithRegistration,
  phoneFormSchemaWithAuth,
  phoneFormSchemaWithRegistration,
  resetPasswordWithEmailSchema,
  createPasswordSchema,
} from '../schemas';
import { usePageVisitData } from '@/shared/hooks';
import { useI18n, useCurrentLocale } from '@locales/client';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { usePhoneMasks } from '@/entities/phone-mask/model/hooks';
import { useProfile } from '@/features/profile/model/hooks';
import { useConfirmApi } from '@/features/data-managment/model/api';
import { useConfirmDataPrompt } from '@/features/data-managment/model/hooks';
import type { AuthContextType } from '@/features/auth-managment/model/auth-context';
import { PhoneField } from '@/shared/ui/form/phone-field';

export const useAuthLogic = (props: {
  visible: boolean;
  onOpen: () => void;
  onClose: () => void;
}) => {
  const {
    authenticate,
    smsAuthenticate,
    resendSms,
    recoveryPasswordWithEmail,
    recoveryPasswordWithPhone,
    createPasswordWithEmail,
    register,
  } = useAuthorization();
  const { visible, onOpen, onClose } = props;

  const { confirmTriggerEmail, confirmTriggerPhone } = useConfirmApi();
  const { removeQueryParam, addQueryParam } = useConfirmDataPrompt();
  const { entryURL, referer } = usePageVisitData();
  const [digits, setDigits] = React.useState(Array(6).fill(''));
  const [viewInfo, setViewInfo] = React.useState(false);
  const locale = useCurrentLocale();
  const { user } = useUser();

  // Состояние формы при регистрации новых пользователей
  const [form, setForm] = React.useState(() => {
    if (typeof window !== 'undefined') {
      const savedForm = sessionStorage.getItem('authForm');
      return savedForm
        ? JSON.parse(savedForm)
        : {
            email: '',
            phone: '',
            password: '',
            isLegalAge: false,
            isNews: true,
          };
    }
    return {
      email: '',
      phone: '',
      password: '',
      isLegalAge: false,
      isNews: true,
    };
  });

  // Состояние формы веклом баннеров при регистрации новых пользователей
  const [formWelcomBanner, setFormWelcomBanner] = React.useState(() => {
    if (typeof window !== 'undefined') {
      const savedBanner = sessionStorage.getItem('authFormWelcomBanner');
      return savedBanner
        ? JSON.parse(savedBanner)
        : {
            currency: '',
            lang: locale || 'en',
            welcomeBonus: true,
            promocode: '',
          };
    }
    return {
      currency: '',
      lang: locale || 'en',
      welcomeBonus: true,
      promocode: '',
    };
  });

  const { trigger: profileTrigger } = useProfile();
  const [currentStep, setCurrentStep] = React.useState('authorization');
  const [spinner, setSpinner] = React.useState(false);
  const [authenticationType, setAuthenticationType] = React.useState('signin');

  const { phoneMasks, isLoading: isPhoneMasksLoading } = usePhoneMasks();
  const [currentCountry, setCurrentCountry] = React.useState({});
  const [currentPhone, setCurrentPhone] = React.useState('');
  const [currentEmail, setCurrentEmail] = React.useState('');
  const searchParams = useSearchParams();
  const [authenticationMethod, setAuthenticationMethod] =
    React.useState('email');
  const inputRefs = useRef([]);
  const t = useI18n();

  const [resetPasswordMethod, setResetPasswordMethod] = React.useState('email');

  const searchParamsValues = {
    modal: searchParams.get('modal'),
    recoveryCode: searchParams.get('recoveryCode'),
  };

  useEffect(() => {
    const authStatus = user?.isAuth;
    const prevForm = sessionStorage.getItem('authForm');
    const prevBanner = sessionStorage.getItem('authFormWelcomBanner');

    if (!authStatus) {
      const currentForm = JSON.stringify(form);
      if (prevForm !== currentForm) {
        sessionStorage.setItem('authForm', currentForm);
      }
    } else if (prevForm) {
      sessionStorage.removeItem('authForm');
    }

    if (!authStatus) {
      const currentBanner = JSON.stringify(formWelcomBanner);
      if (prevBanner !== currentBanner) {
        sessionStorage.setItem('authFormWelcomBanner', currentBanner);
      }
    } else if (prevBanner) {
      sessionStorage.removeItem('authFormWelcomBanner');
    }
  }, [form, formWelcomBanner, user?.isAuth]);

  useEffect(() => {
    if (locale) {
      setFormWelcomBanner((res) => {
        return {
          ...res,
          lang: locale,
        };
      });
    }
  }, [locale]);

  // Инициализация текущей страны
  useEffect(() => {
    if (!isPhoneMasksLoading) {
      setCurrentCountry(
        phoneMasks.find((mask) => mask.default) || phoneMasks[0]
      );
    }
  }, [isPhoneMasksLoading]);

  // Обработка параметров URL
  useEffect(() => {
    const checkEmailCodeRecovery = async () => {
      if (searchParamsValues?.recoveryCode) {
        setAuthenticationType('create-password');
        setCurrentStep('create-recovery-password');
      }
    };
    const checkRegisterModal = async () => {
      if (searchParamsValues?.modal === 'registration') {
        setAuthenticationType('signup');
        setCurrentStep('authorization');
      }

      if (searchParamsValues?.modal === 'verification') {
        if (!user?.phoneConfirm && user?.phone) {
          setCurrentStep('sms-confirmation');
          setAuthenticationType('sms-confirmation');
        } else if (!user?.emailConfirm && user?.email) {
          setCurrentStep('email-confirmation');
          setAuthenticationType('email-confirmation');
        }
      }
    };
    checkEmailCodeRecovery();
    checkRegisterModal();
  }, [searchParams, user]);

  // Обработка изменения телефона пользователя
  useEffect(() => {
    if (user.action === 'edit-phone') {
      onOpen();
      setCurrentPhone(user.phone);
      setCurrentStep('sms-confirmation');
    }
  }, [user]);

  // Закрытие модалки при авторизации
  useEffect(() => {
    if (
      user?.isAuth &&
      visible &&
      searchParamsValues?.modal !== 'verification' &&
      !searchParamsValues?.recoveryCode
    ) {
      handleClose();
    }
  }, [user, visible, searchParams]);

  /**
   * Закрытие модального окна и сброс состояний
   */
  const handleClose = () => {
    setCurrentStep('authorization');
    setResetPasswordMethod('email');
    setAuthenticationType('signin');
    setForm({
      email: '',
      phone: '',
      password: '',
      isLegalAge: false,
      isNews: true,
    });
    setFormWelcomBanner({
      currency: '',
      lang: locale || '',
      welcomeBonus: true,
      promocode: '',
    });

    sessionStorage.removeItem('authForm');
    sessionStorage.removeItem('authFormWelcomBanner');
    removeQueryParam('modal');
    removeQueryParam('recoveryCode');
    onClose();
  };

  /**
   * Основной метод отправки формы
   * @param values - Значения формы
   */
  const onSubmit = async (values: any) => {
    setSpinner(true);

    try {
      if (authenticationType === 'signup') {
        await handleSignup(values);
      } else if (authenticationType === 'signin') {
        await handleSignin(values);
      } else if (authenticationType === 'recovery') {
        await handleRecovery(values);
      } else if (authenticationType === 'create-password') {
        await handleCreatePassword(values);
      } else if (authenticationType === 'create-password-sms') {
        await handleCreatePasswordSMS(values);
      }
    } catch (error) {
      console.error('Error during authentication:', error);
    } finally {
      setSpinner(false);
    }
  };

  /**
   * Обработчик изменения цифры в SMS-коде
   * @param index - Индекс изменяемой цифры
   * @param value - Новое значение
   */
  const handleDigitChange = (index: number, value: string) => {
    if (!/^[a-z\d]?$/i.test(value)) return;

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (newDigits.every((d) => d) && index === 5) {
      if (resetPasswordMethod !== 'phone') {
        const message = Object.values(getValues()).slice(0, 6).join('');
        authenticateSms({ message });
      }
    }
    if (value === '' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  /**
   * Регистрация нового пользователя
   * @param values - Значения формы регистрации
   */
  const handleSignup = async (values: any) => {
    const updatedValues = {
      ...values,
      ...formWelcomBanner,
      ...(authenticationMethod === 'phone' ? { email: '' } : { phone: '' }),
      entryURL,
      referer,
    };

    const authHandler = {
      phone: () => {
        const fullPhone = `${currentCountry.code}${values.phone}`;
        return handleAuthSignup(
          updatedValues,
          fullPhone,
          setCurrentPhone,
          () => register({ ...updatedValues, phone: fullPhone }),
          confirmTriggerPhone
        );
      },
      email: () =>
        handleAuthSignup(
          updatedValues,
          values.email,
          setCurrentEmail,
          () => register(updatedValues),
          confirmTriggerEmail
        ),
    };

    if (authHandler[authenticationMethod]) {
      await authHandler[authenticationMethod]();
    }
  };

  /**
   * Вспомогательный метод для регистрации
   * @param values - Значения формы
   * @param credential - Телефон или email
   * @param setCredential - Установщик состояния для учетных данных
   * @param registerFn - Функция регистрации
   * @param confirmTrigger - Функция подтверждения
   */
  const handleAuthSignup = async (
    values: any,
    credential: string,
    setCredential: React.Dispatch<React.SetStateAction<string>>,
    registerFn: () => Promise<any>,
    confirmTrigger: () => Promise<any>
  ) => {
    try {
      setCredential(credential);
      const response = await registerFn();

      if (response?.success) {
        addQueryParam('verification');
        if (values?.welcomeBonus) {
          sessionStorage.setItem('welcomeBonus', JSON.stringify(true));
          sessionStorage.removeItem('authForm');
          sessionStorage.removeItem('authFormWelcomBanner');
        }

        // await confirmTrigger();
      }
    } catch (error) {
      console.error('Registration error:', error);
    }
  };

  /**
   * Авторизация пользователя
   * @param values - Значения формы авторизации
   */
  const handleSignin = async (values: any) => {
    if (authenticationMethod === 'email') {
      await authenticate(values, 'loginWithEmail');
    } else if (authenticationMethod === 'phone') {
      setCurrentPhone(`${values.phone}`);
      await authenticate(
        { ...values, phone: currentCountry.code + values.phone },
        'loginWithPhone'
      );
    }
    handleClose();
  };

  /**
   * Восстановление пароля
   * @param values - Значения формы восстановления
   */
  const handleRecovery = async (values: any) => {
    if (resetPasswordMethod === 'email') {
      const response = await recoveryPasswordWithEmail(values);
      if (response.success) {
        setForm(values);
        setCurrentStep('confirm-password-email');
      }
    } else if (resetPasswordMethod === 'phone') {
      setCurrentPhone(`${values.phone}`);
      const response = await recoveryPasswordWithPhone({
        phone: currentCountry.code + values.phone,
      });
      if (response.success) {
        setForm(values);
        setCurrentStep('create-password-sms');
        setAuthenticationType('create-password-sms');
      }
    }
  };

  /**
   * Создание нового пароля (через email)
   * @param values - Значения формы пароля
   */
  const handleCreatePassword = async (values: any) => {
    const data = {
      ...values,
      code: searchParamsValues?.recoveryCode,
    };
    delete data.confirmPassword;

    await createPasswordWithEmail(data);
    handleClose();
  };

  /**
   * Создание нового пароля (через SMS)
   * @param values - Значения формы пароля
   */
  const handleCreatePasswordSMS = async (values: any) => {
    const message = Object.values(getValues())
      .map((val) => val.toUpperCase())
      .join('');

    const data = {
      ...values,
      code: message,
      phone: currentCountry.code + currentPhone,
    };
    delete data.confirmPassword;

    const response = await createPasswordWithEmail(data);
    if (response.success) {
      handleClose();
    }
  };

  /**
   * Подтверждение SMS-кода
   */
  const handleSmsSubmit = async () => {
    await confirmTriggerPhone();
  };

  /**
   * Аутентификация по SMS
   * @param value - Значение SMS-кода
   */
  const authenticateSms = async (value: any) => {
    await smsAuthenticate(value);
    handleClose();
    window.location.reload();
  };

  /**
   * Повторная отправка SMS
   * @param values - Дополнительные значения (опционально)
   */
  const handleSmsResend = async (values?: any) => {
    if (user.action === 'edit-phone') {
      const formData = new FormData();
      formData.append('phone', currentCountry.code + user.phone);
      await profileTrigger(formData);
    } else {
      resendSms({ ...form, phone: currentCountry.code + currentPhone });
    }
  };

  /**
   * Повторная отправка email
   */
  const handleEmailResend = async () => {
    await confirmTriggerEmail();
  };

  /**
   * Обработчик изменения SMS-поля
   * @param index - Индекс поля
   * @param value - Значение поля
   */
  const handleSmsChange = (index: number, value: string) => {
    const valid = SmsConfirmSchema.safeParse(value);

    if (valid.success) {
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
      if (value && index > 5) {
        inputRefs.current[index - 1]?.focus();
      }

      if (index === 5 && allInputsFilled()) {
        if (resetPasswordMethod !== 'phone') {
          const message = Object.values(getValues()).slice(0, 6).join('');
          authenticateSms({ message });
        }
      }
    }

    if (value === '' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  /**
   * Обработчик нажатия клавиш в SMS-поле
   * @param index - Индекс поля
   * @param e - Событие клавиатуры
   */
  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  /**
   * Проверка заполненности всех полей кода
   * @returns boolean - Все ли поля заполнены
   */
  const allInputsFilled = (): boolean => {
    return range(6).every(
      (index) => SmsConfirmSchema.safeParse(getValues()[`code${index}`]).success
    );
  };

  /**
   * Получение значений всех полей кода
   * @returns Объект со значениями полей
   */
  const getValues = (): Record<string, string> => {
    return inputRefs.current.reduce(
      (values, inputRef, index) => {
        values[`code${index}`] = inputRef.value;
        return values;
      },
      {} as Record<string, string>
    );
  };

  // Компонент поля для ввода телефона
  const PhoneFieldCmp = React.forwardRef<HTMLInputElement>((props, ref) => {
    return (
      <PhoneField
        ref={ref}
        country={currentCountry}
        onCountryChange={setCurrentCountry}
        {...props}
      />
    );
  });

  // Функции валидации
  const validatePhoneFormWithAuth = validateSchema(phoneFormSchemaWithAuth(t));
  const validatePhoneFormWithRegistration = validateSchema(
    phoneFormSchemaWithRegistration(t)
  );
  const validateEmailFormWithAuth = validateSchema(emailFormSchemaWithAuth(t));
  const validateEmailFormWithRegistration = validateSchema(
    emailFormSchemaWithRegistration(t)
  );

  /**
   * Основная функция валидации
   * @param values - Значения для валидации
   * @returns Результат валидации
   */
  const validate = (values: any) => {
    if (authenticationType === 'signup') {
      if (authenticationMethod === 'email') {
        return validateEmailFormWithRegistration(values);
      }
      if (authenticationMethod === 'phone') {
        return validatePhoneFormWithRegistration(values);
      }
    }
    if (authenticationType === 'signin') {
      if (authenticationMethod === 'phone') {
        return validatePhoneFormWithAuth(values);
      }
      if (authenticationMethod === 'email') {
        return validateEmailFormWithAuth(values);
      }
    }
  };

  // Специализированные функции валидации
  const validateResetPasswordWithEmail = validateSchema(
    resetPasswordWithEmailSchema(t)
  );
  const validateResetPasswordWithPhone = validateSchema(
    resetPasswordWithPhoneSchema(t)
  );
  const validateCreatePassword = validateSchema(createPasswordSchema(t));

  /**
   * Обработчик сброса пароля
   */
  const handleResetPassword = () => {
    if (authenticationMethod === 'email') {
      setResetPasswordMethod('email');
    }
    if (authenticationMethod === 'phone') {
      setResetPasswordMethod('phone');
    }
    setCurrentStep('reset-password-email');
    setAuthenticationType('recovery');
  };

  // Значение контекста
  return {
    currentStep,
    authenticationType,
    authenticationMethod,
    resetPasswordMethod,
    spinner,
    form,
    setForm,
    currentCountry,
    currentPhone,
    currentEmail,
    inputRefs,
    t,
    searchParams,
    setCurrentStep,
    setAuthenticationType,
    setAuthenticationMethod,
    setResetPasswordMethod,
    handleClose,
    onSubmit,
    handleResetPassword,
    handleSmsChange,
    handleSmsSubmit,
    handleSmsResend,
    handleEmailResend,
    PhoneFieldCmp,
    validate,
    validateResetPasswordWithEmail,
    validateResetPasswordWithPhone,
    validateCreatePassword,
    handleDigitChange,
    handleKeyDown,
    digits,
    setDigits,
    user,
    formWelcomBanner,
    setFormWelcomBanner,
    viewInfo,
    setViewInfo,
  } as AuthContextType;
};
